import { useCallback, useEffect, useRef, useState } from 'react';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';
import type { FlowExecution } from '@shared/types/flow-executions';
import type { ActivityActionLaunchStatus, ActivityLaunchTone } from './ActivityActionQueue';
import type { ActivityInlineToolLaunchResult } from './ActivityInlineToolLauncher';

const LAUNCH_RESULT_DISPLAY: Record<
  ActivityInlineToolLaunchResult['status'],
  { label: string; tone: ActivityLaunchTone }
> = {
  running: { label: 'En cours', tone: 'running' },
  blocked: { label: 'À compléter', tone: 'blocked' },
  missing_one_thing: { label: 'À compléter', tone: 'blocked' },
  workspace_ready: { label: 'Terminé', tone: 'done' },
  ready_immediately: { label: 'Prêt', tone: 'neutral' },
};

const EXECUTION_STATUS_DISPLAY: Record<
  string,
  { label: string; tone: ActivityLaunchTone; terminal: boolean }
> = {
  pending: { label: 'En cours', tone: 'running', terminal: false },
  running: { label: 'En cours', tone: 'running', terminal: false },
  paused: { label: 'À compléter', tone: 'blocked', terminal: false },
  completed: { label: 'Terminé', tone: 'done', terminal: true },
  failed: { label: 'Échec', tone: 'failed', terminal: true },
  cancelled: { label: 'Annulée', tone: 'neutral', terminal: true },
};

/**
 * Tracks tools launched inline from the Home cards: seeds the CTA status chip
 * at launch, then follows the run over Realtime so the chip flips to
 * Terminé / À compléter / Échec without leaving the page.
 */
export const useInlineToolLaunchStatuses = (options?: {
  onTerminal?: () => void;
}) => {
  const [launchStatuses, setLaunchStatuses] = useState<
    Record<string, ActivityActionLaunchStatus>
  >({});
  const channelsRef = useRef<Map<string, RealtimeChannel>>(new Map());
  const onTerminalRef = useRef(options?.onTerminal);

  useEffect(() => {
    onTerminalRef.current = options?.onTerminal;
  }, [options?.onTerminal]);

  const stopWatching = useCallback((executionId: string) => {
    const channel = channelsRef.current.get(executionId);
    if (!channel) return;
    channelsRef.current.delete(executionId);
    void supabase.removeChannel(channel);
  }, []);

  const watchExecution = useCallback(
    (params: { actionId: string; executionId: string; href: string }) => {
      if (channelsRef.current.has(params.executionId)) return;

      const channel = supabase
        .channel(`home-inline-run-${params.executionId}`)
        .on(
          'postgres_changes',
          {
            event: 'UPDATE',
            schema: 'public',
            table: 'flow_executions',
            filter: `id=eq.${params.executionId}`,
          },
          (payload) => {
            const updated = payload.new as FlowExecution | null;
            const display = updated?.status
              ? EXECUTION_STATUS_DISPLAY[updated.status]
              : undefined;
            if (!display) return;

            setLaunchStatuses((currentStatuses) => ({
              ...currentStatuses,
              [params.actionId]: {
                label: display.label,
                tone: display.tone,
                href: params.href,
              },
            }));

            if (display.terminal) {
              stopWatching(params.executionId);
              onTerminalRef.current?.();
            }
          }
        )
        .subscribe();

      channelsRef.current.set(params.executionId, channel);
    },
    [stopWatching]
  );

  const registerLaunch = useCallback(
    (actionId: string, result: ActivityInlineToolLaunchResult) => {
      const toolPath = `/tools/${encodeURIComponent(result.toolKey)}`;
      const href = result.executionId
        ? `${toolPath}?run=${encodeURIComponent(result.executionId)}`
        : toolPath;
      const display = LAUNCH_RESULT_DISPLAY[result.status];

      setLaunchStatuses((currentStatuses) => ({
        ...currentStatuses,
        [actionId]: {
          label: display.label,
          tone: display.tone,
          href,
        },
      }));

      if (
        result.executionId &&
        (result.status === 'running' || result.status === 'blocked')
      ) {
        watchExecution({ actionId, executionId: result.executionId, href });
      }
    },
    [watchExecution]
  );

  useEffect(
    () => () => {
      channelsRef.current.forEach((channel) => {
        void supabase.removeChannel(channel);
      });
      channelsRef.current.clear();
    },
    []
  );

  return { launchStatuses, registerLaunch };
};
